import { getRcTokenBalance, getRcAllBalances, getRcTokens, getRcChains } from '@/api/trade';
import { readRcBalanceAmount, readRcBalanceDetailAmount } from '@/utils/platform-balance';
import { buildRcChainsForSymbol } from '@/utils/rc-chain-map';
import { getExpectedChainId } from '@/utils/wallet-chain';

const DEFAULT_RC_SYMBOL = 'USDC';
const TOKEN_ID_CACHE_MS = 5 * 60 * 1000;

/** @type {{ symbol: string, tokenId: string, expires: number }} */
const tokenIdCache = { symbol: '', tokenId: '', expires: 0 };

export function invalidateRcUsdcTokenIdCache() {
  tokenIdCache.symbol = '';
  tokenIdCache.tokenId = '';
  tokenIdCache.expires = 0;
}

/**
 * tokenId 来源（OpenAPI，与 DepositDialog 充值一致）：
 * 1. 环境变量 VUE_APP_RC_USDC_TOKEN_ID
 * 2. GET /rc/tokens + GET /rc/chains → buildRcChainsForSymbol
 * 3. GET /rc/balances 返回项中的 tokenId（兜底）
 */
export async function resolveRcUsdcTokenId(symbol = DEFAULT_RC_SYMBOL) {
  const sym =
    String(symbol ?? DEFAULT_RC_SYMBOL)
      .trim()
      .toUpperCase() || DEFAULT_RC_SYMBOL;

  const fromEnv = String(process.env.VUE_APP_RC_USDC_TOKEN_ID ?? '').trim();
  if (fromEnv) return fromEnv;

  if (tokenIdCache.symbol === sym && tokenIdCache.tokenId && tokenIdCache.expires > Date.now()) {
    return tokenIdCache.tokenId;
  }

  const [tokens, chains] = await Promise.all([getRcTokens(), getRcChains()]);
  const rcChains = buildRcChainsForSymbol(tokens, chains, sym);
  if (rcChains.length) {
    const preferredChainId = String(getExpectedChainId());
    const onPreferredChain = rcChains.find((row) => String(row.chainId) === preferredChainId);
    const picked = onPreferredChain || rcChains[0];
    const tokenId = String(picked?.tokenId ?? '').trim();
    if (tokenId) {
      tokenIdCache.symbol = sym;
      tokenIdCache.tokenId = tokenId;
      tokenIdCache.expires = Date.now() + TOKEN_ID_CACHE_MS;
      return tokenId;
    }
  }

  try {
    const list = await getRcAllBalances();
    const rows = (Array.isArray(list) ? list : []).filter(
      (row) =>
        String(row?.symbol ?? '')
          .trim()
          .toUpperCase() === sym
    );
    const withTokenId = rows.find((row) => String(row?.tokenId ?? '').trim());
    if (withTokenId?.tokenId) {
      const tokenId = String(withTokenId.tokenId).trim();
      tokenIdCache.symbol = sym;
      tokenIdCache.tokenId = tokenId;
      tokenIdCache.expires = Date.now() + TOKEN_ID_CACHE_MS;
      return tokenId;
    }
  } catch (err) {
    console.warn('[rc-platform-balance] rc/balances lookup failed', err);
  }

  throw new Error(`RC tokenId not found for ${sym}`);
}

/** 从 GET /rc/balances 汇总指定 symbol 的平台可用余额 */
export async function fetchRcSymbolBalanceFromList(symbol = DEFAULT_RC_SYMBOL) {
  const sym =
    String(symbol ?? DEFAULT_RC_SYMBOL)
      .trim()
      .toUpperCase() || DEFAULT_RC_SYMBOL;
  const list = await getRcAllBalances();
  const rows = (Array.isArray(list) ? list : []).filter(
    (row) =>
      String(row?.symbol ?? '')
        .trim()
        .toUpperCase() === sym
  );

  if (!rows.length) return null;

  let total = 0;
  let tokenId = '';
  for (const row of rows) {
    total += readRcBalanceDetailAmount(row);
    if (!tokenId && row?.tokenId) tokenId = String(row.tokenId).trim();
  }

  return {
    available: total,
    tokenId,
    rows,
    source: 'rc-balances',
  };
}

/** GET /rc/balance/{tokenId} — path 参数 tokenId 由 resolveRcUsdcTokenId 解析 */
export async function fetchRcSymbolBalanceByTokenId(symbol = DEFAULT_RC_SYMBOL) {
  const sym =
    String(symbol ?? DEFAULT_RC_SYMBOL)
      .trim()
      .toUpperCase() || DEFAULT_RC_SYMBOL;
  const tokenId = await resolveRcUsdcTokenId(sym);
  const data = await getRcTokenBalance(tokenId);
  return {
    available: readRcBalanceAmount(data),
    tokenId: String(data?.tokenId ?? tokenId).trim(),
    source: 'rc-balance',
  };
}

/**
 * 平台 USDC 余额（OpenAPI）
 * 主接口：GET /rc/balance/{tokenId}（path 必传 tokenId）
 * 兜底：GET /rc/balances
 */
export async function fetchRcPlatformUsdcBalance(symbol = DEFAULT_RC_SYMBOL) {
  const sym =
    String(symbol ?? DEFAULT_RC_SYMBOL)
      .trim()
      .toUpperCase() || DEFAULT_RC_SYMBOL;

  try {
    return await fetchRcSymbolBalanceByTokenId(sym);
  } catch (err) {
    console.warn('[rc-platform-balance] rc/balance/{tokenId} failed', err);
  }

  const fromList = await fetchRcSymbolBalanceFromList(sym);
  if (fromList && Number.isFinite(fromList.available)) {
    return fromList;
  }

  throw new Error(`RC platform balance unavailable for ${sym}`);
}
